import type { VectorTool } from "@frontend/pages/case-workspace/pathology/types"
import { useCallback, useEffect, useRef, useState } from "react"

export type RadialView = "arc" | "settings"

// Selector rendered size — must match SVG_PX in radial-tool-selector.tsx
export const SELECTOR_PX = 203
// Offset from cursor when opening (arc origin near pointer)
const OPEN_OFFSET_PX = 24

interface UseRadialToolSelectorOptions {
  containerRef: React.RefObject<HTMLDivElement | null>
  enabled: boolean
  onDismissWithTool?: (tool: VectorTool | null) => void
}

interface UseRadialToolSelectorReturn {
  isOpen: boolean
  view: RadialView
  setView: (view: RadialView) => void
  anchor: { x: number; y: number }
  close: () => void
  selectorRef: React.RefObject<HTMLDivElement | null>
}

export function useRadialToolSelector({
  containerRef,
  enabled,
  onDismissWithTool
}: UseRadialToolSelectorOptions): UseRadialToolSelectorReturn {
  const [isOpen, setIsOpen] = useState(false)
  const isOpenRef = useRef(false)
  const [view, setView] = useState<RadialView>("arc")
  const [anchor, setAnchor] = useState({ x: 0, y: 0 })
  const pointerRef = useRef({ x: 0, y: 0 })
  const rectRef = useRef<DOMRect | null>(null)
  const selectorRef = useRef<HTMLDivElement | null>(null)

  // Cache container rect via ResizeObserver (avoids getBoundingClientRect on every mousemove)
  useEffect(() => {
    const container = containerRef.current
    if (!(container && enabled)) return
    rectRef.current = container.getBoundingClientRect()
    const ro = new ResizeObserver(() => {
      rectRef.current = container.getBoundingClientRect()
    })
    ro.observe(container)
    return () => ro.disconnect()
  }, [containerRef, enabled])

  // Track pointer position on the container (using ref, no re-renders)
  useEffect(() => {
    const container = containerRef.current
    if (!(container && enabled)) return

    const handleMouseMove = (e: MouseEvent) => {
      if (isOpenRef.current) return // pause tracking while open
      const rect = rectRef.current
      if (!rect) return
      pointerRef.current.x = e.clientX - rect.left
      pointerRef.current.y = e.clientY - rect.top
    }

    container.addEventListener("mousemove", handleMouseMove)
    return () => container.removeEventListener("mousemove", handleMouseMove)
  }, [containerRef, enabled])

  const close = useCallback(() => {
    setIsOpen(false)
    isOpenRef.current = false
    setView("arc")
  }, [])

  // Keyboard listener — uses isOpenRef to avoid re-registering on open/close
  useEffect(() => {
    if (!enabled) {
      if (isOpenRef.current) close()
      return
    }

    const handleKeyDown = (e: KeyboardEvent) => {
      // Guard: no modifiers, not in input/textarea
      const tag = (e.target as HTMLElement)?.tagName
      if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") return
      if (e.ctrlKey || e.metaKey || e.altKey) return

      if (e.key === "t" || e.key === "T") {
        e.preventDefault()
        if (isOpenRef.current) {
          close()
        } else {
          const container = containerRef.current
          if (!container) return
          const rect = container.getBoundingClientRect()
          const pad = 8

          let x = pointerRef.current.x - OPEN_OFFSET_PX
          let y = pointerRef.current.y + OPEN_OFFSET_PX

          x = Math.max(pad, Math.min(x, rect.width - SELECTOR_PX - pad))
          y = Math.max(pad, Math.min(y, rect.height - SELECTOR_PX - pad))

          setAnchor({ x, y })
          setView("arc")
          setIsOpen(true)
          isOpenRef.current = true
        }
        return
      }

      if (isOpenRef.current && e.key === "Escape") {
        e.preventDefault()
        close()
        return
      }

      // P/B/E/S: switch tool (and dismiss radial if open)
      const toolForKey: Record<string, VectorTool> = {
        p: "pen",
        b: "brush",
        e: "erase",
        s: "sam"
      }
      const tool = toolForKey[e.key.toLowerCase()]
      if (tool) {
        e.preventDefault()
        onDismissWithTool?.(tool)
        if (isOpenRef.current) close()
      }
    }

    document.addEventListener("keydown", handleKeyDown)
    return () => document.removeEventListener("keydown", handleKeyDown)
  }, [enabled, close, containerRef, onDismissWithTool])

  // Click outside to close
  useEffect(() => {
    if (!isOpen) return

    const handleMouseDown = (e: MouseEvent) => {
      if (selectorRef.current && !selectorRef.current.contains(e.target as Node)) {
        close()
      }
    }

    document.addEventListener("mousedown", handleMouseDown)
    return () => document.removeEventListener("mousedown", handleMouseDown)
  }, [isOpen, close])

  return {
    isOpen,
    view,
    setView,
    anchor,
    close,
    selectorRef
  }
}
